/**
 * Typed calls into the Rust side of the dictation vocabulary. Rust is the one
 * runtime source: it corrects every transcript, normalises tokens, and
 * classifies words with the system spell checker. Token indices count
 * whitespace-separated tokens; a range is `from` (inclusive) to `to`
 * (exclusive).
 */
import { invoke } from "@tauri-apps/api/core";
import type { VocabularyWord } from "@/api/voice-vocabulary";
import { isTauri } from "@/lib/is-tauri";

/** The mishearing a token range names, and its neighbours, as Rust
 *  normalises them for learning. */
export type LearnedContext = { heard: string; left: string; right: string };

/** Replaces the account vocabulary; Rust always retains built-in product spelling. */
export async function setDictationVocabulary(
  words: readonly VocabularyWord[],
): Promise<void> {
  if (!isTauri()) return;
  await invoke("set_dictation_vocabulary", {
    words: words.map((word) => ({
      text: word.text,
      real_word: word.real_word,
      heard_as: word.heard_as.map((heard) => ({
        text: heard.text,
        real_word: heard.real_word,
        contexts: heard.contexts,
      })),
    })),
  });
}

/** Whether the text is real words in the speech language. Without a checker
 *  for the language, or outside the desktop app, the answer is true: the
 *  conservative side, which never replaces a word outside its context. */
export async function classifyVoiceText(
  text: string,
  locale: string | null,
): Promise<boolean> {
  if (!isTauri()) return true;
  return invoke<boolean>("classify_heard_as", { text, locale });
}

export async function learnedContext(
  text: string,
  from: number,
  to: number,
): Promise<LearnedContext> {
  if (!isTauri()) throw new Error("Learning a word needs the desktop app");
  return invoke<LearnedContext>("dictation_vocabulary_learned_context", {
    text,
    from,
    to,
  });
}

/** Replaces a token range of a recent dictation with the right word. */
export async function fixDictationAnywhereWord(
  historyId: string,
  from: number,
  to: number,
  word: string,
): Promise<void> {
  if (!isTauri()) throw new Error("Fixing a dictation needs the desktop app");
  await invoke("dictation_anywhere_fix_word", { historyId, from, to, word });
}
